import { Accordion, Box, Flex, Group, Table, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import SectionCard from 'src/cashflows/components/SectionCard';
import { formatCurrency } from 'src/cashflows/utils';
import { formatDate } from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FinancialRow, FinancialSubSection } from './financialRows';

const GRAY_1_BORDER = '1px solid var(--mantine-color-gray-1)';
const CHEVRON_SIZE = 20;
const CHEVRON_GAP = 8;

type FinancialSectionTableProps = {
  title: string;
  rows: FinancialRow[];
  total: number;
  totalLabel?: string;
  subSections?: FinancialSubSection[];
  showMember?: boolean;
  onRowClick?: (row: FinancialRow) => void;
};

export function FinancialSectionTable({
  title,
  rows,
  total,
  totalLabel = 'Total',
  subSections,
  showMember = false,
  onRowClick,
}: FinancialSectionTableProps) {
  const [openValue, setOpenValue] = useState<string | null>(null);

  if (rows.length === 0) {
    return null;
  }

  const isOpen = openValue === title;

  return (
    <SectionCard
      value={title}
      title={title}
      count={rows.length}
      countNoun={{ one: 'item', many: 'items' }}
      titleMt={4}
      rightSectionPlacement="inline"
      openValue={openValue}
      onOpenChange={setOpenValue}
      styles={{
        item: { border: GRAY_1_BORDER, borderRadius: 12, background: 'transparent' },
        control: { backgroundColor: 'var(--mantine-color-gray-0)' },
        chevron: { display: 'none' },
        content: { paddingTop: 32, paddingBottom: 32, paddingLeft: 24, paddingRight: 24 },
      }}
      rightSection={
        <Box>
          <Text
            size="xs"
            fw={FONT_WEIGHTS.semibold}
            c="gray.4"
            ta="right"
            pr={CHEVRON_SIZE + CHEVRON_GAP}
          >
            {totalLabel}
          </Text>
          <Group gap={CHEVRON_GAP} wrap="nowrap" align="center" justify="flex-end" mt={4}>
            <Text size="lg" fw={FONT_WEIGHTS.bold} c="gray.10">
              {formatCurrency(total)}
            </Text>
            <IconChevronDown
              size={CHEVRON_SIZE}
              stroke={1.5}
              color="var(--mantine-color-gray-9)"
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Group>
        </Box>
      }
    >
      {subSections && subSections.length > 0 ? (
        <FinancialSubSections
          sections={subSections}
          showMember={showMember}
          onRowClick={onRowClick}
        />
      ) : (
        <FinancialRows rows={rows} showMember={showMember} onRowClick={onRowClick} />
      )}
    </SectionCard>
  );
}

function FinancialSubSections({
  sections,
  showMember,
  onRowClick,
}: {
  sections: FinancialSubSection[];
  showMember: boolean;
  onRowClick?: (row: FinancialRow) => void;
}) {
  return (
    <Accordion
      multiple
      chevron={<IconChevronDown size={18} stroke={1.5} color="var(--mantine-color-gray-9)" />}
      styles={{
        item: { border: 'none', background: 'transparent' },
        control: { backgroundColor: 'transparent', paddingLeft: 0, paddingRight: 0 },
        content: { paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 0 },
      }}
    >
      {sections.map(section => (
        <Accordion.Item role="region" key={section.key} value={section.key}>
          <Accordion.Control>
            <Flex justify="space-between" align="center">
              <Text size="md" fw={FONT_WEIGHTS.bold} c="gray.10">
                {section.title}
              </Text>
              <Text size="md" fw={FONT_WEIGHTS.bold} c="gray.10">
                {formatCurrency(section.total)}
              </Text>
            </Flex>
          </Accordion.Control>
          <Accordion.Panel>
            <FinancialRows rows={section.rows} showMember={showMember} onRowClick={onRowClick} />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}

function FinancialRows({
  rows,
  showMember,
  onRowClick,
}: {
  rows: FinancialRow[];
  showMember: boolean;
  onRowClick?: (row: FinancialRow) => void;
}) {
  const columns = [
    ...(showMember ? ['Member'] : []),
    'Description',
    'Provider',
    'Type',
    'Balance date',
    'Balance',
  ];

  return (
    <Table
      withRowBorders={false}
      verticalSpacing={0}
      horizontalSpacing="md"
      styles={{ thead: { backgroundColor: 'transparent' } }}
    >
      <Table.Thead>
        <Table.Tr>
          {columns.map((column, index) => (
            <Table.Th
              key={column}
              style={{
                paddingBottom: 16,
                textAlign: index === columns.length - 1 ? 'right' : 'left',
              }}
            >
              <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6">
                {column}
              </Text>
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map(row => (
          <Table.Tr
            key={row.id}
            style={{ borderTop: GRAY_1_BORDER, cursor: onRowClick ? 'pointer' : undefined }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {showMember && <RowCell>{row.memberName}</RowCell>}
            <RowCell>{row.description}</RowCell>
            <RowCell>{row.provider}</RowCell>
            <RowCell>{row.typeLabel}</RowCell>
            <RowCell>{row.balanceDate ? formatDate(row.balanceDate) : undefined}</RowCell>
            <RowCell align="right">
              {row.balance != null ? formatCurrency(row.balance) : undefined}
            </RowCell>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function RowCell({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <Table.Td style={{ paddingTop: 16, paddingBottom: 20, textAlign: align }}>
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
        {children || '—'}
      </Text>
    </Table.Td>
  );
}
